/**
 * Anmeldung der Verwaltungsseite — und das Setzen des Passworts über den
 * Einmal-Link aus der Einladung.
 *
 * Eine Seite für drei Fälle: anmelden, Passwort vergessen, Passwort
 * setzen. Der Beirat sieht sie nie; er kommt über seinen Link.
 */

import { useEffect, useState } from 'react';

import { api } from '../lib/api';
import type { Konto } from '../lib/typen';

export function Anmeldung({ passwortToken, onAngemeldet }: { passwortToken?: string | null; onAngemeldet: (konto: Konto) => void }) {
  const [modus, setModus] = useState<'anmelden' | 'vergessen' | 'setzen'>(passwortToken ? 'setzen' : 'anmelden');
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [linkPerson, setLinkPerson] = useState<{ email: string; name: string } | null>(null);

  useEffect(() => {
    if (!passwortToken) return;
    api.konto
      .passwortLinkPruefen(passwortToken)
      .then(setLinkPerson)
      .catch((g) => {
        setFehler((g as Error).message);
        setModus('anmelden');
      });
  }, [passwortToken]);

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    if (laeuft) return;
    setLaeuft(true);
    setFehler(null);
    setHinweis(null);
    try {
      if (modus === 'anmelden') {
        onAngemeldet(await api.konto.anmelden(email.trim(), passwort));
      } else if (modus === 'vergessen') {
        const antwort = await api.konto.passwortVergessen(email.trim());
        setHinweis(antwort.meldung);
      } else if (passwortToken) {
        if (passwort !== wiederholung) {
          setFehler('Die beiden Passwörter stimmen nicht überein.');
          return;
        }
        onAngemeldet(await api.konto.passwortSetzen(passwortToken, passwort));
      }
    } catch (g) {
      setFehler((g as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="anmeldung">
      <div className="anmeldung-karte card">
        <div className="marke" style={{ marginBottom: 18 }}>
          <div className="brandmark">iD</div>
          <div>
            <div className="marke-titel">Beiratsprüfung</div>
            <div className="marke-unter">Verwaltungsseite</div>
          </div>
        </div>

        {modus === 'setzen' ? (
          <>
            <h2 style={{ margin: '0 0 4px' }}>Passwort setzen</h2>
            <p className="s" style={{ marginTop: 0 }}>
              {linkPerson ? `Für ${linkPerson.name} (${linkPerson.email}).` : 'Der Link wird geprüft …'} Mindestens zehn Zeichen — ein
              Satz ist besser als ein Wort.
            </p>
          </>
        ) : modus === 'vergessen' ? (
          <>
            <h2 style={{ margin: '0 0 4px' }}>Passwort vergessen</h2>
            <p className="s" style={{ marginTop: 0 }}>Sie bekommen einen Link, mit dem Sie ein neues Passwort setzen.</p>
          </>
        ) : (
          <>
            <h2 style={{ margin: '0 0 4px' }}>Anmelden</h2>
            <p className="s" style={{ marginTop: 0 }}>
              Für Hausverwaltungen. Beiräte öffnen den Link aus ihrer Einladungsmail.
            </p>
          </>
        )}

        <form onSubmit={absenden}>
          {modus !== 'setzen' && (
            <label className="feld">
              <span>E-Mail</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus />
            </label>
          )}
          {modus !== 'vergessen' && (
            <label className="feld">
              <span>{modus === 'setzen' ? 'Neues Passwort' : 'Passwort'}</span>
              <input
                type="password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                autoComplete={modus === 'setzen' ? 'new-password' : 'current-password'}
                required
                minLength={modus === 'setzen' ? 10 : undefined}
                autoFocus={modus === 'setzen'}
              />
            </label>
          )}
          {modus === 'setzen' && (
            <label className="feld">
              <span>Passwort wiederholen</span>
              <input type="password" value={wiederholung} onChange={(e) => setWiederholung(e.target.value)} autoComplete="new-password" required />
            </label>
          )}

          {fehler && <p style={{ color: 'var(--err)', fontSize: 13, margin: '0 0 12px' }}>{fehler}</p>}
          {hinweis && <p style={{ color: 'var(--ok)', fontSize: 13, margin: '0 0 12px' }}>{hinweis}</p>}

          <button className="btn primary" type="submit" disabled={laeuft || (modus === 'setzen' && !linkPerson)} style={{ width: '100%' }}>
            {laeuft ? 'Einen Moment …' : modus === 'setzen' ? 'Passwort setzen und anmelden' : modus === 'vergessen' ? 'Link anfordern' : 'Anmelden'}
          </button>
        </form>

        <div style={{ marginTop: 14, fontSize: 12.5 }}>
          {modus === 'anmelden' ? (
            <button className="link" onClick={() => { setModus('vergessen'); setFehler(null); }}>
              Passwort vergessen?
            </button>
          ) : (
            <button className="link" onClick={() => { setModus('anmelden'); setFehler(null); setHinweis(null); }}>
              Zur Anmeldung
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
